/**
 * Buyer library — list of templates the user has purchased.
 * Clicking an item opens the full document.
 */
import React, { useCallback, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { ArrowLeft, BookOpen, Loader2, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

export default function MarketplaceLibrary() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState(null);

  const allowed = user && (user.subscription_tier === "legal_pro" || user.subscription_tier === "business");

  useImperativeLoad(() => {
    if (!allowed) { setLoading(false); return; }
    api.get("/marketplace/purchases/me").then((r) => {
      setItems(r.data.items || []);
      setLoading(false);
    });
  }, [allowed]);

  const handleOpen = useCallback(async (item) => {
    try {
      const r = await api.get(`/marketplace/purchases/${item.purchase_id}/document`);
      setOpened(r.data);
    } catch (e) {
      toast.error("Could not load document");
    }
  }, []);

  if (!allowed) {
    return (
      <div className="flex min-h-full items-center justify-center bg-[#0A0C1F] p-10 text-white">
        <p>Legal Pro tier required.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-[#0A0C1F] p-10 text-white">
        <Loader2 className="h-6 w-6 animate-spin text-[#9ACD32]" />
      </div>
    );
  }

  if (opened) {
    return (
      <div className="h-full overflow-y-auto bg-[#0A0C1F] text-white print:bg-white" data-testid="library-detail">
        <section className="mx-auto max-w-4xl px-6 py-10 lg:px-12">
          <button
            type="button"
            onClick={() => setOpened(null)}
            className="mb-6 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white print:hidden"
            data-testid="library-detail-back"
          >
            <ArrowLeft className="h-3 w-3" /> {t("marketplace.detail_back")}
          </button>
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#F4C955]">{opened.label}</p>
          <h1 className="mt-1 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">
            {opened.title}
          </h1>
          <p className="mt-1 text-xs text-slate-500">
            {t("marketplace.card_by")} {opened.author_display_name} · {formatDate(opened.paid_at)}
          </p>
          <Card className="mt-8 border-slate-800 bg-white p-10 text-[#1a1a1a] shadow-2xl print:border-0 print:shadow-none">
            <pre className="whitespace-pre-wrap font-serif text-[14px] leading-[1.7]" data-testid="library-detail-document">
              {opened.anonymized_document}
            </pre>
          </Card>
        </section>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-[#0A0C1F] text-white" data-testid="marketplace-library-page">
      <section className="mx-auto max-w-5xl px-6 py-10 lg:px-12">
        <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
          {t("marketplace.library_title")}
        </h1>

        {items.length === 0 ? (
          <Card className="mt-8 border-slate-800 bg-slate-900/60 p-12 text-center" data-testid="library-empty">
            <ShoppingBag className="mx-auto h-10 w-10 text-slate-600" />
            <p className="mx-auto mt-3 max-w-md text-sm text-slate-400">{t("marketplace.library_empty")}</p>
            <Link to="/marketplace" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#9ACD32] px-5 py-2 text-sm font-bold text-[#05073B] hover:bg-[#A8DF38]">
              {t("marketplace.cta_browse")}
            </Link>
          </Card>
        ) : (
          <div className="mt-6 space-y-3" data-testid="library-list">
            {items.map((it) => (
              <Card key={it.purchase_id} className="flex items-center justify-between border-slate-800 bg-slate-900/60 p-5" data-testid={`library-item-${it.purchase_id}`}>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#F4C955]">{it.label}</p>
                  <p className="truncate font-display text-base font-bold text-white">{it.title}</p>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                    {t("marketplace.card_by")} {it.author_display_name} · {formatDate(it.paid_at)} · €{it.price_eur}
                  </p>
                </div>
                <Button
                  onClick={() => handleOpen(it)}
                  size="sm"
                  className="rounded-full bg-[#D4A937] text-[#05073B] hover:bg-[#E0B850]"
                  data-testid={`library-open-${it.purchase_id}`}
                >
                  <BookOpen className="mr-1.5 h-3.5 w-3.5" /> {t("marketplace.library_open")}
                </Button>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
